import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { promptRefinementService, type RefinementSessionEnriched } from '../../services/promptRefinementService';
import { FilterableTable } from '../../components/ui';
import type { FilterableColumnDef } from '../../components/ui';
import { PageContainer } from '../../components/layout';

const STATUS_LABELS: Record<string, string> = {
  pending: 'En attente',
  generated: 'Suggestions générées',
  accepted: 'Suggestions acceptées',
  applied: 'Candidat créé',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-700',
  generated: 'bg-blue-100 text-blue-700',
  accepted: 'bg-yellow-100 text-yellow-700',
  applied: 'bg-green-100 text-green-700',
};

export function OptimisationPromptsPage() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<RefinementSessionEnriched[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const data = await promptRefinementService.listSessions();
      setSessions(data);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-CA', {
      timeZone: 'America/Montreal',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns: FilterableColumnDef<RefinementSessionEnriched>[] = useMemo(() => [
    {
      key: 'id',
      header: 'Session',
      searchable: true,
      sortValueFn: (s) => s.id,
      render: (s) => <span className="text-xs font-mono text-gray-500">{s.id.slice(0, 8)}</span>,
    },
    {
      key: 'note',
      header: 'Note',
      sortable: true,
      searchable: true,
      sortValueFn: (s) => s.note || '',
      render: (s) => <span className="text-sm">{s.note || '—'}</span>,
    },
    {
      key: 'tags',
      header: 'Tags',
      filterable: true,
      filterValueFn: (s) => s.tags || [],
      render: (s) => (
        <div className="flex flex-wrap gap-1">
          {(s.tags || []).map((tag) => (
            <span key={tag} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">{tag}</span>
          ))}
        </div>
      ),
    },
    {
      key: 'domain',
      header: 'Domaine',
      filterable: true,
      filterValueFn: (s) => s.run_domain || '',
      render: (s) => s.run_domain || '—',
    },
    {
      key: 'run',
      header: 'Run',
      searchable: true,
      sortValueFn: (s) => s.benchmark_run_id,
      render: (s) => (
        <div>
          <div className="text-xs font-mono text-gray-600">{s.benchmark_run_id.slice(0, 8)}</div>
          {s.run_note && <div className="text-xs text-gray-400">({s.run_note})</div>}
        </div>
      ),
    },
    {
      key: 'source_prompt',
      header: 'Prompt source',
      searchable: true,
      sortValueFn: (s) => s.source_prompt_version || '',
      render: (s) => s.source_prompt_name
        ? <div><div className="text-sm">v{s.source_prompt_version}</div><div className="text-xs text-gray-400">({s.source_prompt_name})</div></div>
        : <span className="text-gray-400">—</span>,
    },
    {
      key: 'source_schema',
      header: 'Schéma source',
      searchable: true,
      sortValueFn: (s) => s.source_schema_version || '',
      render: (s) => s.source_schema_name
        ? <div><div className="text-sm">v{s.source_schema_version}</div><div className="text-xs text-gray-400">({s.source_schema_name})</div></div>
        : <span className="text-gray-400">—</span>,
    },
    {
      key: 'candidates',
      header: 'Candidats',
      render: (s) => (
        <div className="flex gap-1">
          {s.candidate_schema_name && (
            <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs">
              Schéma v{s.candidate_schema_version}
            </span>
          )}
          {s.candidate_prompt_name && (
            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
              Prompt v{s.candidate_prompt_version}
            </span>
          )}
          {!s.candidate_schema_name && !s.candidate_prompt_name && (
            <span className="text-gray-400 text-xs">—</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Statut',
      filterable: true,
      filterValueFn: (s) => s.status,
      filterOptions: [
        { value: 'pending', label: 'En attente' },
        { value: 'generated', label: 'Suggestions générées' },
        { value: 'accepted', label: 'Acceptées' },
        { value: 'applied', label: 'Candidat créé' },
      ],
      render: (s) => (
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[s.status] || ''}`}>
          {STATUS_LABELS[s.status] || s.status}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Date',
      sortable: true,
      sortValueFn: (s) => s.created_at,
      render: (s) => <span className="text-sm text-gray-600">{formatDate(s.created_at)}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (s) => (
        <button
          onClick={() => navigate(`/benchmark/optimisation/${s.id}`)}
          className="text-primary hover:underline text-sm"
        >
          Ouvrir
        </button>
      ),
    },
  ], [navigate]);

  return (
    <PageContainer>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Optimisation de prompts et schémas d'extraction</h1>
        <button
          onClick={() => navigate('/benchmark/optimisation/new')}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-600"
        >
          + Nouvelle session
        </button>
      </div>

      {loading ? (
        <div>Chargement...</div>
      ) : (
        <FilterableTable
          persistKey="benchmark-optimisation-prompts"
          paginated
          data={sessions}
          columns={columns}
          rowKey={(s) => s.id}
          defaultSort={{ key: 'created_at', direction: 'desc' }}
          noDataMessage="Aucune session d'optimisation"
          emptyMessage="Aucune session ne correspond aux filtres"
        />
      )}
    </PageContainer>
  );
}
